import React, { forwardRef } from 'react';
import { Controller } from 'react-hook-form';
import { Autocomplete, Checkbox, TextField } from '@mui/material';
import { Colors, FindLoad } from 'utils';
import { InputMinLoader } from '../../inputs';

const getNestedError = (obj, path) => {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

export const SelectAutocompleteMultiple = forwardRef(
    (
        {
            control,
            rules,
            label,
            options = [],
            placeholder,
            name,
            disabled,
            renderValue,
            classNameWrapper,
            multiple = false,
            selectedType,
            loadType,
            fullValue,
            defaultSelected,
            handleChange,
            renderCustomRow,
            readOnly,
            variant = 'outlined',
            disableClearAble,
            groupBy,
            renderGroup,
            // For options fetched into local component state, where there is no
            // saga loadType to look up.
            loading,
            // Optional per-option disable (MUI Autocomplete's own prop), e.g. a row that is listed
            // for context but cannot be picked.
            getOptionDisabled,
            // Wrap a long picked option onto more lines instead of cutting it with "…" (single select).
            multiline,
        },
        ref
    ) => {
        const loadTypeLoader = FindLoad(loadType);
        const loader = loading ? [true] : loadTypeLoader;

        return (
            <Controller
                control={control}
                name={name}
                rules={rules}
                render={({ field, formState: { errors } }) => {
                    const error = getNestedError(errors, name);
                    const current =
                        defaultSelected && field.value
                            ? defaultSelected
                            : field.value
                              ? options?.find(
                                    (i) =>
                                        i.id ===
                                        (field?.value?.id
                                            ? field?.value?.id
                                            : field?.value?._id
                                              ? field?.value?._id
                                              : i?.id === field?.value
                                                ? i?.id
                                                : field?.value)
                                )
                              : null;

                    const selectedValues = multiple ? (Array.isArray(field.value) ? field.value : defaultSelected || []) : current;

                    return (
                        <div style={{ width: '100%', opacity: disabled ? 0.5 : 1 }}>
                            <Autocomplete
                                {...field}
                                disableClearable={disableClearAble}
                                value={selectedValues}
                                ref={ref}
                                key={options || field.value}
                                loading={!!loader?.length}
                                disabled={disabled || readOnly}
                                className={`autocomplete-main-wrapper ${classNameWrapper ? classNameWrapper : ''}`}
                                multiple={multiple}
                                id="checkboxes-tags-demo"
                                options={options || []}
                                getOptionDisabled={getOptionDisabled}
                                groupBy={groupBy}
                                renderGroup={renderGroup}
                                disableCloseOnSelect={multiple}
                                onChange={(event, value) => {
                                    field.onChange(multiple ? value : fullValue ? value : value?.id || value?._id);
                                    handleChange && handleChange(value);
                                }}
                                getOptionLabel={(option) => {
                                    return option ? (renderValue ? renderValue(option) : option?.title) : '';
                                }}
                                isOptionEqualToValue={(option, value) => {
                                    const optionId = option?.id || option?._id || option?.value;
                                    const valueId = value?.id || value?._id || value?.value;
                                    return optionId === valueId;
                                }}
                                renderOption={(props, option, { selected }) => {
                                    const { key, ...restProps } = props;
                                    return (
                                        <li key={key} {...restProps}>
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                {multiple && (
                                                    <Checkbox
                                                        style={{ padding: '2px 8px 2px 0', color: Colors.ThemeBlue }}
                                                        checked={selected}
                                                    />
                                                )}
                                                {renderCustomRow ? (
                                                    renderCustomRow(option)
                                                ) : (
                                                    <div>
                                                        <p style={{ width: '100%' }}>{renderValue ? renderValue(option) : option.title}</p>
                                                        {name === 'permissions' && (
                                                            <p style={{ color: 'gray', fontSize: '12px' }}>{option?.description}</p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </li>
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        multiline={multiline}
                                        // A textarea would take Enter as a new line; Autocomplete still picks the
                                        // highlighted option on Enter (its root handler does not check defaultPrevented).
                                        onKeyDown={multiline ? (e) => e.key === 'Enter' && e.preventDefault() : undefined}
                                        slotProps={{
                                            input: !!loader?.length
                                                ? {
                                                      ...params.InputProps,
                                                      endAdornment: <InputMinLoader />,
                                                  }
                                                : { ...params.InputProps },
                                        }}
                                        variant={variant}
                                        label={label}
                                        placeholder={placeholder}
                                        error={!!error?.message}
                                    />
                                )}
                            />
                            <p className="error-message">{error?.message}</p>
                        </div>
                    );
                }}
            />
        );
    }
);

SelectAutocompleteMultiple.displayName = 'SelectAutocompleteMultiple';
